import PropTypes from 'prop-types';
import React, { Component } from 'react';
import queryString from 'query-string';
import { connect } from 'react-redux';
import { Link } from 'react-router';

import { HYDRA_CHECKOUT_EXPOSE_SHIP_OPTIONS, HYDRA_CHECKOUT_MOVE_TO_FAVORITES, HYDRA_CHECKOUT_SHIPPING_DOWNGRADE } from 'constants/hydraTests';
import { evMelodyPlaceOrder, pvMelodyCheckout } from 'events/checkout';
import {
  CART_STEP,
  CHECKOUT_STEP_MAP,
  GIFT_OPTIONS_STEP,
  REVIEW_STEP,
  TBD_STEP
} from 'constants/melodyCheckoutFlow';

import { isAssigned, isInAssignment, triggerAssignment } from 'actions/ab';
import { setHeaderFooterVisibility } from 'actions/headerfooter';
import { titaniteView, track } from 'apis/amethyst';
import { PageLoader } from 'components/Loader';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import { inIframe, isReadyToSubmitMelodyCheckout } from 'helpers/CheckoutUtils';
import ChargeSummary from 'components/checkout/melody/ChargeSummary';
import OrderTotal from 'components/checkout/melody/OrderTotal/OrderTotal';
import Review from 'containers/Checkout/melody/Review';
import ReviewItems from 'components/checkout/melody/review/ReviewItems';
import GiftOptions from 'containers/Checkout/melody/GiftOptions';
import PaymentMethod from 'containers/Checkout/melody/PaymentMethod';
import ShippingAddress from 'containers/Checkout/melody/ShippingAddress';
import ShippingOptions from 'containers/Checkout/melody/ShippingOptions';
import UseAsDefaults from 'containers/Checkout/melody/UseAsDefaults';
import SectionDivider from 'components/checkout/melody/SectionDivider';
import Note from 'components/checkout/melody/Note';
import { isDesktop } from 'helpers/ClientUtils';
import { onEvent, supportsPassiveEventListener } from 'helpers/EventHelpers';
import {
  configurePurchase,
  onChangeQuantityEvent,
  onFetchAkitaEstimate,
  onHideGiftOptionsClick,
  onMaxStepIsCartStep,
  onMoveToFavoritesClick,
  onRequestRedeemableRewards,
  onSendToDesiredPage,
  onSendToMaxAvailableStep,
  onToggleItemsEvent,
  onViewGiftOptionsClick,
  placeCheckoutOrder,
  resetCheckout,
  trackEventNotMaxAvailable
} from 'store/ducks/checkout/actions';
import {
  getCleanPath,
  getStepFromPath
} from 'helpers/MelodyCheckoutFlowControl';
import css from 'styles/containers/checkout/melodyCheckout.scss';
import marketplace from 'cfg/marketplace.json';

const { checkout: { melodyHydraTest } } = marketplace;

const defaultState = {
  giftMessage: null,
  lineItemId: null,
  giftWrapId: null
};

export class MelodyCheckout extends Component {
  static webpackChunkName = 'Checkout';
  state = defaultState;

  componentDidMount() {
    const {
      configurePurchase,
      onFetchAkitaEstimate,
      onRequestRedeemableRewards,
      resetCheckout,
      melodyCheckoutData: { isLoaded },
      location: { search },
      wasLastPageOrderConfirmation,
      triggerAssignment } = this.props;
    const { marketplace: { checkout: { allowRewardsRedemption, melodyHydraTest }, hasShippingDowngrades } } = this.context;
    const { pid } = queryString.parse(search);

    triggerAssignment(melodyHydraTest);
    const hydraCheckoutShippingDowngrade = triggerAssignment(HYDRA_CHECKOUT_SHIPPING_DOWNGRADE);
    triggerAssignment(HYDRA_CHECKOUT_MOVE_TO_FAVORITES);
    const hydraCheckoutExposeShipOptions = triggerAssignment(HYDRA_CHECKOUT_EXPOSE_SHIP_OPTIONS);

    const opts = supportsPassiveEventListener() ? { passive: true } : false;
    onEvent(window, 'resize', this.onResize, opts, this);
    this.onResize();

    if (wasLastPageOrderConfirmation) {
      return;
    }

    if (isLoaded) {
      resetCheckout();
    }

    const includeShipmentOptions = isInAssignment(hydraCheckoutExposeShipOptions) || isInAssignment(hydraCheckoutShippingDowngrade) && hasShippingDowngrades;

    configurePurchase({ purchaseId: pid, includePaymentsAndAddresses: true, includeShipmentOptions });

    if (includeShipmentOptions) {
      onFetchAkitaEstimate();
    }

    if (allowRewardsRedemption && !inIframe()) {
      onRequestRedeemableRewards();
    }
  }

  componentDidUpdate(prevProps) {
    const { melodyCheckoutData: { isLoaded: prevIsLoaded }, location: { pathname: prevPathname } } = prevProps;
    const { trackEventNotMaxAvailable, onMaxStepIsCartStep, onSendToDesiredPage, onSendToMaxAvailableStep, melodyCheckoutData, melodyCheckoutData: { maxAvailableStep, isLoaded: nextIsLoaded }, location: { pathname: nextPathname, query } } = this.props;
    const isJustLoaded = nextIsLoaded && prevIsLoaded !== nextIsLoaded;

    if (!nextIsLoaded || !nextPathname.includes('checkout')) {
      return;
    }

    if (isJustLoaded) {
      titaniteView();
      track(pvMelodyCheckout, { ...melodyCheckoutData });
    }

    if (maxAvailableStep === CART_STEP) {
      onMaxStepIsCartStep();
      return;
    }

    const stepForPrevPath = getStepFromPath(prevPathname);
    const stepForNextPath = getStepFromPath(nextPathname);

    if (stepForNextPath > maxAvailableStep) {
      onSendToMaxAvailableStep(maxAvailableStep);
      return;
    } else if (stepForNextPath !== stepForPrevPath) {
      trackEventNotMaxAvailable(stepForNextPath);
    }

    if (isJustLoaded) {
      const step = stepForNextPath === TBD_STEP ? REVIEW_STEP : stepForNextPath;
      onSendToDesiredPage(step, query);
    }

    if (prevPathname !== nextPathname && getCleanPath(prevPathname) === CHECKOUT_STEP_MAP[GIFT_OPTIONS_STEP]) {
      this.setState(defaultState);
    }
  }

  componentWillUnmount() {
    this.props.setHeaderFooterVisibility(true);
  }

  onChangeQuantity = (e, lineItemId) => {
    const { configurePurchase, onChangeQuantityEvent, melodyCheckoutData: { isFetchingCheckoutData, productsByLineItem } } = this.props;
    const { target: { value: quantity } } = e;

    if (!isFetchingCheckoutData) {
      onChangeQuantityEvent(quantity, productsByLineItem[lineItemId].quantity, productsByLineItem[lineItemId]);
      const quantityUpdate = { lineItemId, quantity };
      configurePurchase({ quantityUpdate, advanceOnSuccess: false, includeShipmentOptions: quantity === '0' });
    }
  }

  onHideGiftOptionsClick = () => {
    const { onHideGiftOptionsClick } = this.props;
    this.setState({ lineItemId: null, giftMessage: null, giftWrapId: null });
    onHideGiftOptionsClick();
  }

  onToggleItems = isShown => {
    this.props.onToggleItemsEvent(isShown);
  };

  onPlaceOrderClick = () => {
    const { placeCheckoutOrder, melodyCheckoutData } = this.props;
    track(evMelodyPlaceOrder, { ...melodyCheckoutData });
    placeCheckoutOrder();
  }

  onResize = () => {
    const { setHeaderFooterVisibility } = this.props;
    const { marketplace } = this.context;
    const isHideHeaderFooter = inIframe() || !isDesktop(marketplace);
    setHeaderFooterVisibility(!isHideHeaderFooter);
  }

  onViewGiftOptionsClick = e => {
    const { onViewGiftOptionsClick } = this.props;
    const { target: { dataset: { lineItemId, giftMessage = null, giftWrapId = null } } } = e;
    e.preventDefault();
    this.setState({ lineItemId, giftMessage, giftWrapId });
    onViewGiftOptionsClick();
  }

  moveToFavoritesClick = ({ currentTarget: { dataset: { asin, lineItemId } } }) => {
    this.props.onMoveToFavoritesClick({ asin, lineItemId });
  }

  render() {
    const {
      isAssignedExposeShipOptions,
      isAssignedInShippingDowngrade,
      isMainReviewSection,
      melodyCheckoutData: {
        asinErrors,
        constraintViolations,
        isLoading,
        isPlacingOrder,
        purchase: { chargeSummary = {}, nameOnAccount },
        isLoaded
      },
      location: { pathname },
      wasLastPageOrderConfirmation
    } = this.props;
    const currentStep = getStepFromPath(pathname);
    const isOrderReadyToSubmit = !isPlacingOrder && !isLoading && isReadyToSubmitMelodyCheckout({ currentStep, constraintViolations });
    const { total: orderTotal } = chargeSummary;
    const { lineItemId, giftMessage, giftWrapId } = this.state;
    const defaultGiftMessage = `Hi,\nEnjoy your gift!\nFrom ${nameOnAccount}`;
    const displayedMessage = giftMessage === null ? defaultGiftMessage : giftMessage;
    const isReviewStep = getCleanPath(pathname) === CHECKOUT_STEP_MAP[REVIEW_STEP];

    const { marketplace: { hasShippingDowngrades } } = this.context;
    const isEligibleForShippingDowngrade = isAssignedInShippingDowngrade && hasShippingDowngrades;

    if (wasLastPageOrderConfirmation) {
      return (
        <div className={css.orderPlaced}>Your order has been placed.  If you wish to modify or cancel it, please do so from your <Link to="/orders">order history</Link>.</div>
      );
    }

    if (!isLoaded) {
      return (
        <PageLoader />
      );
    }

    const reviewSection = <ReviewItems
      isMainReviewSection={isMainReviewSection}
      isReviewStep={isReviewStep}
      onChangeQuantity={this.onChangeQuantity}
      onMoveToFavoritesClick={this.moveToFavoritesClick}
      onToggleItems={this.onToggleItems}
      onViewGiftOptionsClick={this.onViewGiftOptionsClick} />;

    return (
      <SiteAwareMetadata>
        <div className={css.page}>
          <div className={css.container}>
            <div className={css.spcWrapper}>
              <GiftOptions
                onHideGiftOptionsClick={this.onHideGiftOptionsClick}
                lineItemId={lineItemId}
                giftMessage={displayedMessage}
                giftWrapId={giftWrapId} />
              <ShippingAddress />
              <SectionDivider />
              <PaymentMethod />
              <SectionDivider />
              { // re-mount is important!
                isReviewStep && <UseAsDefaults /> }
              <ShippingOptions isAssignedExposeShipOptions={isAssignedExposeShipOptions} isEligibleForShippingDowngrade={isEligibleForShippingDowngrade} />
              { !isEligibleForShippingDowngrade && <SectionDivider /> }
              {
                isMainReviewSection && <Review reviewSection={reviewSection} />
              }
              {
                isMainReviewSection && <SectionDivider />
              }
              <OrderTotal
                isOrderReadyToSubmit={isOrderReadyToSubmit}
                isPlacingOrder={isPlacingOrder}
                onPlaceOrderClick={this.onPlaceOrderClick}
                orderTotal={orderTotal} />
              <SectionDivider />
              <Note />
            </div>
            <div>
              <div className={css.chargeSummaryWrapper}>
                <ChargeSummary
                  asinErrors={asinErrors}
                  isMainReviewSection={isMainReviewSection}
                  isOrderReadyToSubmit={isOrderReadyToSubmit}
                  isPlacingOrder={isPlacingOrder}
                  isReviewStep={isReviewStep}
                  onPlaceOrderClick={this.onPlaceOrderClick}
                  reviewSection={reviewSection}
                />
              </div>
            </div>
          </div>
        </div>
      </SiteAwareMetadata>
    );
  }
}

function mapStateToProps(state) {
  const { melodyCheckoutData, routing: { locationBeforeTransitions }, history: { wasLastPageOrderConfirmation } } = state;
  const isAssignedExposeShipOptions = isAssigned(HYDRA_CHECKOUT_EXPOSE_SHIP_OPTIONS, 1, state);
  const isMainReviewSection = isAssigned(melodyHydraTest, 2, state);
  const isAssignedInShippingDowngrade = isAssigned(HYDRA_CHECKOUT_SHIPPING_DOWNGRADE, 1, state);

  return {
    isAssignedExposeShipOptions,
    isAssignedInShippingDowngrade,
    isMainReviewSection,
    melodyCheckoutData,
    wasLastPageOrderConfirmation,
    location: locationBeforeTransitions
  };
}

MelodyCheckout.contextTypes = {
  testId: PropTypes.func,
  marketplace: PropTypes.object
};

export default connect(mapStateToProps, {
  configurePurchase,
  onChangeQuantityEvent,
  onFetchAkitaEstimate,
  onHideGiftOptionsClick,
  onMaxStepIsCartStep,
  onMoveToFavoritesClick,
  onRequestRedeemableRewards,
  onSendToDesiredPage,
  onSendToMaxAvailableStep,
  onViewGiftOptionsClick,
  onToggleItemsEvent,
  placeCheckoutOrder,
  resetCheckout,
  setHeaderFooterVisibility,
  trackEventNotMaxAvailable,
  triggerAssignment
})(MelodyCheckout);
